import { useCallback, useEffect, useRef } from 'react';
import type { Point } from './geometrieCarte';
import type { ProfondeurIci } from './profondeurDuDossier';

/** Une molette donne plusieurs crans d'un coup : un seul ouvre ou referme (maquette). */
export const MOLETTE_APAISEE_MS = 350;

/**
 * Les gestes qui ouvrent une étape sur place (ticket 06 de retours-testeurs,
 * design/carte.html) : la petite flèche, et la molette sur une bulle.
 *
 * - Ouvrir : l'étape s'ouvre par-dessus la profondeur choisie (ticket 13, `profondeur.ts`),
 *   et la caméra se centre sur elle. Tout de suite sur sa
 *   place d'avant, puis sur celle que le serveur renvoie si elle a bougé (quand beaucoup
 *   d'étapes sont ouvertes, `domain/carte/carte.ts` peut l'avoir écartée).
 * - Refermer : l'étape se referme, avec ce qu'elle contenait, et le reste jusqu'au prochain
 *   changement de profondeur.
 * - La molette sur une bulle qui a des étapes : vers l'avant elle s'ouvre, vers l'arrière
 *   elle se referme. Sur une bulle qui n'a rien dedans, sur le centre ou sur les commandes :
 *   rien, ni ouverture ni zoom (décision d'Igor du 2026-09-26). Ailleurs, c'est la caméra
 *   qui zoome.
 *
 * Ce fichier ne calcule aucune place : il dit ce qui est ouvert, le serveur place.
 */
export function useGestesDeDepliage({
  profondeur,
  enfantsDe,
  ouvertes,
  visibles,
  actifs,
  places,
  placesFraiches,
  ouEst,
  centrerSur,
}: {
  profondeur: ProfondeurIci;
  /** Les étapes connues de chaque bulle : une bulle s'ouvre si elle en a. */
  enfantsDe: Map<string, string[]>;
  /** Ce qui est ouvert sur cette feuille, tel que la liste le dit. */
  ouvertes: ReadonlySet<string>;
  visibles: ReadonlySet<string>;
  /** Faux pendant qu'on hiérarchise : on désigne, on n'ouvre rien. */
  actifs: boolean;
  places: Record<string, Point>;
  /** Les places viennent du serveur pour la liste d'aujourd'hui, pas d'une liste d'avant. */
  placesFraiches: boolean;
  ouEst: (id: string) => Point;
  centrerSur: (p: Point) => void;
}) {
  const aCentrer = useRef<string | null>(null);
  const { ouvrir: ouvrirIci, fermer: fermerIci } = profondeur;

  const ouvrir = useCallback(
    (id: string) => {
      ouvrirIci(id);
      aCentrer.current = id;
      centrerSur(ouEst(id));
    },
    [ouvrirIci, ouEst, centrerSur]
  );

  const replier = useCallback(
    (id: string) => {
      if (aCentrer.current === id) aCentrer.current = null;
      fermerIci(id);
    },
    [fermerIci]
  );

  // Les places arrivées, la caméra se recentre si l'étape ouverte a bougé.
  useEffect(() => {
    const id = aCentrer.current;
    if (!id || !placesFraiches) return;
    aCentrer.current = null;
    const place = places[id];
    if (place) centrerSur(place);
  }, [places, placesFraiches, centrerSur]);

  const basculer = useCallback(
    (id: string) => (ouvertes.has(id) ? replier(id) : ouvrir(id)),
    [ouvertes, replier, ouvrir]
  );

  const derniereMolette = useRef(0);
  /** La molette : `true` si elle était pour une bulle (la caméra ne zoome pas alors). */
  const traiterLaMolette = (evenement: WheelEvent): boolean => {
    const cible = evenement.target as Element;
    if (cible.closest('.carte-commandes, .tableau-centre')) return true;
    const noeud = cible.closest<HTMLElement>('.noeud[data-bulle]');
    if (!noeud) return false;
    const id = noeud.dataset.bulle ?? '';
    if (!actifs || !enfantsDe.has(id) || !visibles.has(id)) return true;
    const maintenant = Date.now();
    if (maintenant - derniereMolette.current < MOLETTE_APAISEE_MS) return true;
    const ouverte = ouvertes.has(id);
    if (evenement.deltaY < 0 && !ouverte) ouvrir(id);
    else if (evenement.deltaY > 0 && ouverte) replier(id);
    else return true;
    derniereMolette.current = maintenant;
    return true;
  };

  return { basculer, traiterLaMolette };
}
